import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import logo from "../../assets/img/AvoConsulte-Logo.svg";
import { urls, currentLang as pageLang, currentPath, switchLanguage } from "../../shared/seo/links";
import logoWhite from "../../assets/img/Logo Avoconsulte white.svg";
import { getTheme, toggleTheme, onThemeChange } from "../../shared/theme";
import "./chrome.css";

const LANGUAGES = [
  { code: "ar", key: "arabic" },
  { code: "fr", key: "french" },
  { code: "en", key: "english" },
];

function readUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch (e) {
    return null;
  }
}

function dashboardPath(user) {
  return user?.role === "lawyer" ? "/Lawyer-Dashboard" : "/User-Dashboard";
}

// Closes a popover when the pointer lands outside it or Escape is pressed.
function useDismiss(ref, open, close) {
  useEffect(() => {
    if (!open) return undefined;
    function onPointer(e) {
      if (ref.current && !ref.current.contains(e.target)) close();
    }
    function onKey(e) {
      if (e.key === "Escape") close();
    }
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("touchstart", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("touchstart", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [ref, open, close]);
}

export default function NavBar() {
  const { t } = useTranslation();
  const location = useLocation();
  const lang = pageLang();
  const [user, setUser] = useState(readUser);
  const [raised, setRaised] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [theme, setThemeState] = useState(getTheme);
  const langRef = useRef(null);
  const userRef = useRef(null);

  const home = urls.homePath(lang);
  const links = [
    { path: home, label: t("Home") },
    { path: urls.sectionPath("lawyers", lang), label: t("Lawyers") },
    { path: urls.sectionPath("questions", lang), label: t("LegalAdvice") },
    { path: urls.sectionPath("blogs", lang), label: t("LegalMagazine") },
    { path: urls.sectionPath("about", lang), label: t("AboutUs") },
    { path: urls.sectionPath("contact", lang), label: t("ContactUs") },
  ];

  // Compared decoded: the Arabic routes are percent-encoded in the address bar.
  const current = currentPath(location.pathname).toLowerCase();
  function isActive(path) {
    const target = path.toLowerCase();
    return target === home.toLowerCase()
      ? current === target
      : current === target || current.startsWith(target + "/");
  }

  useEffect(() => {
    function onScroll() {
      setRaised(window.scrollY > 8);
    }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Another tab may sign in or out; the dashboards also rewrite the user.
  useEffect(() => {
    function onStorage() {
      setUser(readUser());
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    setDrawerOpen(false);
    setLangOpen(false);
    setUserOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.classList.toggle("avc-drawer-locked", drawerOpen);
    if (!drawerOpen) return undefined;
    function onKey(e) {
      if (e.key === "Escape") setDrawerOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.classList.remove("avc-drawer-locked");
    };
  }, [drawerOpen]);

  useEffect(() => onThemeChange(setThemeState), []);

  useDismiss(langRef, langOpen, () => setLangOpen(false));
  useDismiss(userRef, userOpen, () => setUserOpen(false));

  // Same page in the other language (from the hreflang links), full load so
  // the other direction's stylesheet applies.
  function handleLng(lng) {
    switchLanguage(lng);
  }

  function handleLogout() {
    localStorage.removeItem("user");
    localStorage.removeItem("jwtAccessToken");
    localStorage.removeItem("jwtRefreshToken");
    window.location.href = urls.toHref(home);
  }

  const currentLang = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];
  const isDark = theme === "dark";
  const brand = isDark ? logoWhite : logo;
  const themeLabel = t(isDark ? "theme_light" : "theme_dark");
  const firstName = user?.firstName || user?.lastName || "";
  const initial = (firstName || "A").trim().charAt(0).toUpperCase();

  function renderLinks(extraClass) {
    return links.map((link) => (
      <li key={link.path}>
        <a
          href={urls.toHref(link.path)}
          className={
            "avc-header__link" +
            (isActive(link.path) ? " avc-header__link--active" : "") +
            (extraClass ? " " + extraClass : "")
          }
          aria-current={isActive(link.path) ? "page" : undefined}
        >
          {link.label}
        </a>
      </li>
    ));
  }

  return (
    <>

      <header className={"avc-header" + (raised ? " avc-header--raised" : "")}>
        <div className="avc-header__inner">
          <a href={urls.toHref(home)} className="avc-header__logo" aria-label={t("AvoConsulte")}>
            <img src={brand} alt="AvoConsulte" />
          </a>

          <ul className="avc-header__nav">{renderLinks()}</ul>

          <div className="avc-header__actions">
            <button
              type="button"
              className="avc-menu__trigger avc-header__theme"
              aria-label={themeLabel}
              title={themeLabel}
              aria-pressed={isDark}
              onClick={toggleTheme}
            >
              <i className={isDark ? "fas fa-sun" : "fas fa-moon"} aria-hidden="true"></i>
            </button>

            <div className="avc-menu" ref={langRef}>
              <button
                type="button"
                className="avc-menu__trigger"
                aria-haspopup="true"
                aria-expanded={langOpen}
                onClick={() => setLangOpen((o) => !o)}
              >
                <i className="fas fa-globe" aria-hidden="true"></i>
                <span className="avc-header__lang-label">{t(currentLang.key)}</span>
                <i className="fas fa-chevron-down avc-menu__caret" aria-hidden="true"></i>
              </button>
              {langOpen && (
                <ul className="avc-menu__list" role="menu">
                  {LANGUAGES.filter((l) => l.code !== currentLang.code).map((l) => (
                    <li key={l.code} role="none">
                      <button
                        type="button"
                        role="menuitem"
                        className="avc-menu__item"
                        onClick={() => handleLng(l.code)}
                      >
                        {t(l.key)}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {user ? (
              <div className="avc-menu" ref={userRef}>
                <button
                  type="button"
                  className="avc-menu__trigger"
                  aria-haspopup="true"
                  aria-expanded={userOpen}
                  onClick={() => setUserOpen((o) => !o)}
                >
                  <span className="avc-avatar" aria-hidden="true">{initial}</span>
                  <span className="avc-header__user-name">{firstName || t("my_account")}</span>
                  <i className="fas fa-chevron-down avc-menu__caret" aria-hidden="true"></i>
                </button>
                {userOpen && (
                  <ul className="avc-menu__list" role="menu">
                    <li role="none">
                      <a href={dashboardPath(user)} role="menuitem" className="avc-menu__item">
                        <i className="fas fa-th-large" aria-hidden="true"></i>
                        {t("Dashboard")}
                      </a>
                    </li>
                    <li role="none" className="avc-menu__divider" aria-hidden="true"></li>
                    <li role="none">
                      <button
                        type="button"
                        role="menuitem"
                        className="avc-menu__item avc-menu__item--danger"
                        onClick={handleLogout}
                      >
                        <i className="fas fa-sign-out-alt" aria-hidden="true"></i>
                        {t("Logout")}
                      </button>
                    </li>
                  </ul>
                )}
              </div>
            ) : (
              <>
                <a href="/login" className="avc-btn avc-btn--outline">
                  {t("Login")}
                </a>
                <a href="/Register" className="avc-btn avc-btn--gold">
                  {t("Register")}
                </a>
              </>
            )}
          </div>

          <button
            type="button"
            className="avc-header__toggle"
            aria-label={t("nav_open_menu")}
            aria-expanded={drawerOpen}
            aria-controls="avc-drawer"
            onClick={() => setDrawerOpen(true)}
          >
            <i className="fas fa-bars" aria-hidden="true"></i>
          </button>
        </div>
      </header>

      <div
        id="avc-drawer"
        className={"avc-drawer" + (drawerOpen ? " avc-drawer--open" : "")}
        aria-hidden={!drawerOpen}
      >
        <div className="avc-drawer__backdrop" onClick={() => setDrawerOpen(false)}></div>
        <div className="avc-drawer__panel" role="dialog" aria-modal="true" aria-label={t("nav_menu")}>
          <div className="avc-drawer__head">
            <a href={urls.toHref(home)} aria-label={t("AvoConsulte")}>
              <img src={brand} alt="AvoConsulte" />
            </a>
            <button
              type="button"
              className="avc-drawer__close"
              aria-label={t("nav_close_menu")}
              onClick={() => setDrawerOpen(false)}
            >
              <i className="fas fa-times" aria-hidden="true"></i>
            </button>
          </div>

          <ul className="avc-drawer__nav">{renderLinks()}</ul>

          <div className="avc-drawer__section">
            <span className="avc-drawer__label">{t("nav_language")}</span>
            <div className="avc-drawer__langs">
              {LANGUAGES.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  className={
                    "avc-drawer__lang" +
                    (l.code === currentLang.code ? " avc-drawer__lang--current" : "")
                  }
                  aria-pressed={l.code === currentLang.code}
                  onClick={() => l.code !== currentLang.code && handleLng(l.code)}
                >
                  {t(l.key)}
                </button>
              ))}
            </div>
          </div>

          <div className="avc-drawer__section">
            <span className="avc-drawer__label">{t("theme_label")}</span>
            <button
              type="button"
              className="avc-drawer__theme"
              aria-pressed={isDark}
              onClick={toggleTheme}
            >
              <i className={isDark ? "fas fa-sun" : "fas fa-moon"} aria-hidden="true"></i>
              {themeLabel}
            </button>
          </div>

          <div className="avc-drawer__actions">
            {user ? (
              <>
                <div className="avc-drawer__user">
                  <span className="avc-avatar" aria-hidden="true">{initial}</span>
                  <span>{firstName || t("my_account")}</span>
                </div>
                <a href={dashboardPath(user)} className="avc-btn avc-btn--gold avc-btn--block">
                  {t("Dashboard")}
                </a>
                <button
                  type="button"
                  className="avc-btn avc-btn--outline avc-btn--block"
                  onClick={handleLogout}
                >
                  {t("Logout")}
                </button>
              </>
            ) : (
              <>
                <a href="/login" className="avc-btn avc-btn--outline avc-btn--block">
                  {t("Login")}
                </a>
                <a href="/Register" className="avc-btn avc-btn--gold avc-btn--block">
                  {t("Register")}
                </a>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
